import { useEffect, useRef, useState } from "react";

/**
 * Optionaler Audio-Reaktivitäts-Hook. Zwei Modi:
 *  - active=false: liefert konstant 0
 *  - active=true:  liefert ein geglättetes Spektrum-Level [0..1]
 *
 * Phase 2a: Audio-Quelle ist ein optionaler MediaStream (Mic-Input oder
 * Sprachsynthese-Stream). Phase 2b speist diese mit der TTS-Engine.
 */
export function useAudioReactive(active: boolean, stream?: MediaStream | null): () => number {
  const levelRef = useRef(0);
  const [, force] = useState(0);
  const targetRef = useRef(0);

  useEffect(() => {
    if (!active || !stream) {
      targetRef.current = 0;
      return;
    }
    const ctx = new AudioContext();
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
    src.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    let raf = 0;
    const tick = (): void => {
      analyser.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i]!;
      targetRef.current = Math.min(1, sum / (data.length * 200));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      void ctx.close();
    };
  }, [active, stream]);

  // Smooth-Lerp zum Target, damit Spitzen den Orb nicht zucken lassen
  useEffect(() => {
    let raf = 0;
    const tick = (): void => {
      levelRef.current += (targetRef.current - levelRef.current) * 0.18;
      force((x) => (x + 1) & 0xfff);
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, []);

  return () => levelRef.current;
}
